import React, { useCallback, useMemo } from 'react';
import { Box, Button, ColorPicker, Flex, Popover, Text } from 'core/components';
import { Field, formConsumer, SegmentedControl, Select, Switch } from 'core/form';
import { observer } from 'mobx-react';
import { TbPlus } from 'react-icons/tb';
import styled from 'styled-components';
import type { ThresholdStep } from 'app/stores/ude/QueryModel';

/** Default threshold colors: green (base) → yellow → orange → red */
const THRESHOLD_COLORS = ['#73BF69', '#F2CC0C', '#FF9830', '#F2495C'];

const DEFAULT_STEPS: ThresholdStep[] = [
  { value: null, color: THRESHOLD_COLORS[0] },
  { value: 50, color: THRESHOLD_COLORS[2] },
  { value: 80, color: THRESHOLD_COLORS[3] }
];

const STEPS_KEY = 'config.chart.thresholds.steps';
const ENABLED_KEY = 'config.chart.thresholds.enabled';

interface UdeThresholdEditorProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
}

const ColorCircle = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => props.color};
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    border-color: rgba(0, 0, 0, 0.3);
    background: ${(props) => props.color}cc; /* add transparency on hover */
  }
`;

const ThresholdRow = styled(Flex)`
  align-items: center;
  height: 25px;
  gap: 8px;
`;

const ValueInput = styled.input`
  flex: 1;
  border: 1px solid;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  min-width: 0;
  border-color: ${(props) => props.theme.colors.cardBorder || '#ddd'};
  background: ${(props) => props.theme.colors.appBackground || '#fff'};
  color: ${(props) => props.theme.colors.body || '#333'};

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary || '#3a80eb'};
  }
`;

function getNextColor(steps: ThresholdStep[]): string {
  const usedColors = new Set(steps.map((s) => s.color));
  return THRESHOLD_COLORS.find((c) => !usedColors.has(c)) || THRESHOLD_COLORS[THRESHOLD_COLORS.length - 1];
}

function getNextValue(steps: ThresholdStep[]): number {
  const numericSteps = steps.filter((s) => s.value !== null).map((s) => s.value as number);
  if (numericSteps.length === 0) {
    return 80;
  }
  const maxVal = Math.max(...numericSteps);
  return maxVal + Math.max(Math.round(maxVal * 0.5), 10);
}

const UdeThresholdEditorInner = observer(({ form }: UdeThresholdEditorProps) => {
  const enabled = form.getValue(ENABLED_KEY) ?? false;
  const rawSteps = form.getValue(STEPS_KEY);
  const steps: ThresholdStep[] = useMemo(
    () => (Array.isArray(rawSteps) && rawSteps.length ? rawSteps : DEFAULT_STEPS),
    [rawSteps]
  );

  const baseStep = steps.find((s) => s.value === null) || { value: null, color: THRESHOLD_COLORS[0] };
  const valueSteps = useMemo(
    () => steps.filter((s) => s.value !== null).sort((a, b) => (b.value as number) - (a.value as number)),
    [steps]
  );

  const updateSteps = useCallback(
    (newSteps: ThresholdStep[]) => {
      form.setValue(STEPS_KEY, newSteps);
      // Sync config to panel model to trigger history tracking and persist via autoSave
      const panel = form.model;
      if (panel) {
        panel.set({ config: form.getValues().config });
      }
      if (form.onChange) {
        form.onChange({ form, formValues: form.getValues() });
      }
    },
    [form]
  );

  const handleAddThreshold = useCallback(() => {
    const newStep: ThresholdStep = {
      value: getNextValue(steps),
      color: getNextColor(steps)
    };
    updateSteps([...steps, newStep]);
  }, [steps, updateSteps]);

  const handleRemoveStep = useCallback(
    (stepValue: number) => {
      updateSteps(steps.filter((s) => s.value !== stepValue));
    },
    [steps, updateSteps]
  );

  const handleValueChange = useCallback(
    (oldValue: number, newValue: string) => {
      const parsed = parseFloat(newValue);
      if (Number.isNaN(parsed)) {
        return;
      }
      updateSteps(steps.map((s) => (s.value === oldValue ? { ...s, value: parsed } : s)));
    },
    [steps, updateSteps]
  );

  const handleColorChange = useCallback(
    (stepValue: number | null, hex: string) => {
      updateSteps(steps.map((s) => (s.value === stepValue ? { ...s, color: hex } : s)));
    },
    [steps, updateSteps]
  );

  return (
    <Flex
      flexDirection="column"
      gap={1}
      style={{ opacity: enabled ? 1 : 0.4, pointerEvents: enabled ? 'auto' : 'none' }}
    >
      {valueSteps.map((step) => (
        <ThresholdRow key={step.value}>
          <Popover
            minimal
            position="bottom-left"
            content={
              <Box p={1}>
                <ColorPicker
                  color={step.color}
                  onChange={({ hex }: { hex: string }) => handleColorChange(step.value, hex)}
                  triangle="hide"
                />
              </Box>
            }
          >
            <ColorCircle color={step.color} />
          </Popover>
          <ValueInput
            type="number"
            defaultValue={step.value as number}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleValueChange(step.value as number, e.target.value)}
          />
          <Button small minimal icon="trash" onClick={() => handleRemoveStep(step.value as number)} />
        </ThresholdRow>
      ))}

      <ThresholdRow>
        <Popover
          minimal
          position="bottom-left"
          content={
            <Box p={1}>
              <ColorPicker
                color={baseStep.color}
                onChange={({ hex }: { hex: string }) => handleColorChange(null, hex)}
                triangle="hide"
              />
            </Box>
          }
        >
          <ColorCircle color={baseStep.color} />
        </Popover>
        <Text fontSize="13px" color="muted" flex={1}>
          Base
        </Text>
      </ThresholdRow>

      <Button small minimal intent="primary" alignSelf="flex-start" icon={TbPlus} onClick={handleAddThreshold}>
        Add threshold
      </Button>

      <Field name="config.chart.thresholds.mode" m={0}>
        <SegmentedControl small fill />
      </Field>

      <Field name="config.chart.thresholds.display" m={0}>
        <Select fill />
      </Field>

      <Field name="config.chart.thresholds.colorSeries" showLabel={false} m={0}>
        <Switch switchLabel="Color entire series" />
      </Field>
    </Flex>
  );
});

const UdeThresholdEditor = formConsumer(UdeThresholdEditorInner);

export { DEFAULT_STEPS, THRESHOLD_COLORS };
export default UdeThresholdEditor;
